import React, { useState, useEffect } from 'react';
import { StyleSheet, View, TouchableOpacity, ActivityIndicator } from 'react-native';
import { ODSText } from '../../atoms/ODSText';
import ODSButton from '../../atoms/ODSButton';
import { ODSInput } from '../../atoms/ODSInput';
import ODSSelectDropdown from '../../atoms/ODSSelectDropdown';
import { getTokens } from '../../../design-tokens/color-tokens';
import getScreenType from '../../../utils/getScreenType';
import { ODSIcon } from '../../atoms/ODSIcon';

const layout = getScreenType();

interface AddressItem {
    BUILDING_NAME?: string;
    BUILDING_NUMBER?: string;
    SUB_BUILDING?: string;
    STREET_NAME?: string;
    LOCALITY?: string;
    POST_TOWN?: string;
    POSTCODE?: string;
    POSTCODE2?: string;
    TOWN_NAME?: string;
    LATITUDE?: number;
    LONGITUDE?: number;
    UPRN?: string;
    OPRID?: string;
    UDPRN?: number;
}

interface ForwardErrors {
    postCode?: string;
    selectedAddress?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    county?: string;
    country?: string;
}

interface ForwardMessegeBoxProps {
    restStyle?: any;
    data?: any;
    onSearchPostCode?: (postCode: string, callback: (addresses: any) => void) => void;
    onSaveForwardingAddress?: (payload: any, callback: () => void) => void;
    customErrors?: ForwardErrors;
}

const ForwardMessegeBox = ({
    restStyle,
    data,
    onSearchPostCode,
    onSaveForwardingAddress,
    customErrors,
}: ForwardMessegeBoxProps) => {
    const themeObj = getTokens('light').colors;

    const [postCode, setPostCode] = useState('');
    const [searching, setSearching] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [noAddressFound, setNoAddressFound] = useState(false);

    const [addressList, setAddressList] = useState<AddressItem[]>([]);
    const [selectedDropdownItem, setSelectedDropdownItem] = useState<any>(null);

    // Form fields
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [city, setCity] = useState('');
    const [county, setCounty] = useState('');
    const [country, setCountry] = useState('UK');

    // Errors state
    const [errors, setErrors] = useState<ForwardErrors>({});

    // UI Steps: 'postcode' | 'dropdown' | 'form' | 'readonly'
    const [step, setStep] = useState<'postcode' | 'dropdown' | 'form' | 'readonly'>('postcode');
    const [tenantConfirm, setTenantConfirm] = useState(false);

    useEffect(() => {
        const resolvedData = data?.data || data;
        const forwardingAddresses = resolvedData?.open_flow_move_out_forwarding_addresses;
        let addressData: any = null;

        if (Array.isArray(forwardingAddresses)) {
            if (forwardingAddresses.length > 0) {
                const sorted = forwardingAddresses.slice().sort((a: any, b: any) => {
                    const idA = a.open_flow_move_out_forwarding_address_id || a.id || 0;
                    const idB = b.open_flow_move_out_forwarding_address_id || b.id || 0;
                    return idB - idA;
                });
                addressData = sorted[0];
            }
        } else if (forwardingAddresses && typeof forwardingAddresses === 'object') {
            addressData = forwardingAddresses;
        }

        if (!addressData) {
            addressData = resolvedData?.open_flow_move_out_forwarding_address || 
                          resolvedData?.forwarding_address;
        }

        if (addressData && addressData.address_line1) {
            setAddressLine1(addressData.address_line1 || '');
            setAddressLine2(addressData.address_line2 || '');
            setCity(addressData.city || '');
            setCounty(addressData.county || addressData.state || '');
            setCountry(addressData.country || 'UK');
            setPostCode(addressData.postcode || '');
            setTenantConfirm(!!(addressData.tenant_confirm || addressData.teanant_confirm));
            setStep('readonly');
        } else {
            setStep('postcode');
            setTenantConfirm(false);
        }
    }, [data]);

    const getFieldError = (field: keyof ForwardErrors): string | undefined => {
        return errors[field] || customErrors?.[field];
    };

    const handleSearch = () => {
        if (!postCode || postCode.trim() === '') {
            setErrors((prev) => ({ ...prev, postCode: 'Post code is required' }));
            return;
        }

        setSearching(true);
        setErrors((prev) => ({ ...prev, postCode: undefined }));
        setNoAddressFound(false);

        if (onSearchPostCode && typeof onSearchPostCode === 'function') {
            onSearchPostCode(postCode.trim(), (results) => {
                setSearching(false);
                const list: AddressItem[] = Array.isArray(results)
                    ? results
                    : Array.isArray(results?.data)
                    ? results.data
                    : Array.isArray(results?.result)
                    ? results.result
                    : [];

                if (list && list.length > 0) {
                    setAddressList(list);
                    setNoAddressFound(false);
                    setStep('dropdown');
                } else {
                    setAddressList([]);
                    setNoAddressFound(true);
                    setErrors((prev) => ({ ...prev, postCode: 'No address found for this post code' }));
                    setStep('postcode');
                }
            });
        } else {
            setSearching(false);
        }
    };

    const handleManualEntry = () => {
        setNoAddressFound(false);
        setErrors((prev) => ({ ...prev, postCode: undefined }));
        setStep('form');
    };

    const handleSelectAddress = (selected: any) => {
        setSelectedDropdownItem(selected);
        setErrors((prev) => ({ ...prev, selectedAddress: undefined }));
        const item: AddressItem = selected.value;

        const line1 = [item.SUB_BUILDING, item.BUILDING_NAME, item.BUILDING_NUMBER, item.STREET_NAME]
            .filter(Boolean)
            .join(', ');

        const line2 = item.TOWN_NAME || item.LOCALITY || item.POST_TOWN || '';
        const town = item.TOWN_NAME || item.POST_TOWN || '';
        const cty = item.LOCALITY || '';
        const pc = item.POSTCODE || postCode;

        setAddressLine1(line1);
        setAddressLine2(line2);
        setCity(town);
        setCounty(cty);
        setCountry('UK');
        setPostCode(pc);

        setStep('form');
    };

    const validateForm = (): boolean => {
        let isValid = true;
        const newErrors: ForwardErrors = {};

        if (!postCode || postCode.trim() === '') {
            newErrors.postCode = 'Post code is required';
            isValid = false;
        }
        if (!addressLine1 || addressLine1.trim() === '') {
            newErrors.addressLine1 = 'Address Line 1 is required';
            isValid = false;
        }
        if (!city || city.trim() === '') {
            newErrors.city = 'City is required';
            isValid = false;
        }
        if (!country || country.trim() === '') {
            newErrors.country = 'Country is required';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleConfirm = () => {
        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        const resolvedData = data?.data || data;
        const payload = {
            openflow_id: resolvedData?.openflow_id || resolvedData?.open_flow?.openflow_id,
            app_user_id: resolvedData?.app_user_id || resolvedData?.tenants?.[0]?.app_user_id,
            address_line1: addressLine1,
            address_line2: addressLine2,
            city: city,
            county: county,
            country: country,
            postcode: postCode,
            teanant_confirm: true,
            tenant_confirm: true,
        };

        if (onSaveForwardingAddress && typeof onSaveForwardingAddress === 'function') {
            onSaveForwardingAddress(payload, () => {
                setSubmitting(false);
                setTenantConfirm(true);
                setStep('readonly');
            });
        } else {
            setSubmitting(false);
            setTenantConfirm(true);
            setStep('readonly');
        }
    };

    const dropdownData = addressList.map((item) => {
        const label = [item.SUB_BUILDING, item.BUILDING_NAME, item.BUILDING_NUMBER, item.STREET_NAME]
            .filter(Boolean)
            .join(', ');
        return {
            label: label || item.STREET_NAME || 'Address',
            value: item,
        };
    });

    return (
        <View style={[styles(themeObj).containerStyle, restStyle]}>
            {step === 'readonly' ? (
                /* STEP 4: READ-ONLY MODE (IMAGE 4) */
                <View style={{ width: '100%' }}>
                    <ODSText textAlign="flex-start" color="black" type="h4" marginBottom={6}>
                        Forwarding Address
                    </ODSText>
                    <ODSText textAlign="flex-start" color={themeObj.text.secondary} type="sm" marginBottom={24}>
                        Please confirm this is your new address.
                    </ODSText>

                    {/* Address Line 1 */}
                    <View style={styles(themeObj).readOnlyFieldGroup}>
                        <ODSText type="sm-bold" color={themeObj.text.primary} marginBottom={4}>
                            Address Line 1
                        </ODSText>
                        <ODSText type="md" color="black">
                            {addressLine1 || '-'}
                        </ODSText>
                    </View>

                    {/* Address Line 2 */}
                    <View style={styles(themeObj).readOnlyFieldGroup}>
                        <ODSText type="sm-bold" color={themeObj.text.primary} marginBottom={4}>
                            Address Line 2
                        </ODSText>
                        <ODSText type="md" color="black">
                            {addressLine2 || '-'}
                        </ODSText>
                    </View>

                    {/* City */}
                    <View style={styles(themeObj).readOnlyFieldGroup}>
                        <ODSText type="sm-bold" color={themeObj.text.primary} marginBottom={4}>
                            City
                        </ODSText>
                        <ODSText type="md" color="black">
                            {city || '-'}
                        </ODSText>
                    </View>

                    {/* State/Country */}
                    <View style={styles(themeObj).readOnlyFieldGroup}>
                        <ODSText type="sm-bold" color={themeObj.text.primary} marginBottom={4}>
                            State/Country
                        </ODSText>
                        <ODSText type="md" color="black">
                            {county || '-'}
                        </ODSText>
                    </View>

                    {/* Country */}
                    <View style={styles(themeObj).readOnlyFieldGroup}>
                        <ODSText type="sm-bold" color={themeObj.text.primary} marginBottom={4}>
                            Country
                        </ODSText>
                        <ODSText type="md" color="black">
                            {country || '-'}
                        </ODSText>
                    </View>

                    {/* Post Code */}
                    <View style={styles(themeObj).readOnlyFieldGroup}>
                        <ODSText type="sm-bold" color={themeObj.text.primary} marginBottom={4}>
                            Post Code
                        </ODSText>
                        <ODSText type="md" color="black">
                            {postCode || '-'}
                        </ODSText>
                    </View>

                    {/* Buttons: Edit & Confirm */}
                    <TouchableOpacity
                        onPress={() => setStep('form')}
                        style={styles(themeObj).outlineButton}
                    >
                        <ODSIcon icon="TB-Edit" size={18} color="#1FA6B2" />
                        <ODSText marginLeft={8} type="md-bold" color="#1FA6B2">
                            Edit
                        </ODSText>
                    </TouchableOpacity>

                    <View style={{ marginTop: 12 }}>
                        <ODSButton
                            restStyle={{ width: '100%', height: 48, borderRadius: 24 }}
                            type="primary"
                            themeColor={tenantConfirm ? '#28B967' : themeObj.main.blue}
                            rIcon={tenantConfirm ? 'TB-Check' : undefined}
                            onPress={handleConfirm}
                            disabled={tenantConfirm || submitting}
                        >
                            {tenantConfirm ? 'Confirmed' : (submitting ? 'Confirming...' : 'Confirm')}
                        </ODSButton>
                    </View>
                </View>
            ) : (
                /* EDITABLE MODES (STEPS 1, 2, 3) */
                <View style={{ width: '100%' }}>
                    <ODSText textAlign="flex-start" color="black" type="h4" marginBottom={6}>
                        Forwarding Address
                    </ODSText>
                    <ODSText textAlign="flex-start" color={themeObj.text.secondary} type="sm" marginBottom={24}>
                        Please add your forwarding address and click confirm to send it to your agent.
                    </ODSText>

                    {/* Post Code Search Block */}
                    <View style={{ marginBottom: 20 }}>
                        <ODSInput
                            label="Post Code"
                            placeholder="Enter post code"
                            value={postCode}
                            error={!!getFieldError('postCode')}
                            hints={
                                getFieldError('postCode')
                                    ? [{ hint: getFieldError('postCode')!, state: 'error' }]
                                    : undefined
                            }
                            onChange={(e: any) => {
                                setPostCode(e?.target?.value ?? e);
                                if (errors.postCode) setErrors((prev) => ({ ...prev, postCode: undefined }));
                            }}
                            onChangeText={(t: string) => {
                                setPostCode(t);
                                if (errors.postCode) setErrors((prev) => ({ ...prev, postCode: undefined }));
                            }}
                        />

                        <View style={{ marginTop: 12 }}>
                            <ODSButton
                                restStyle={{ width: '100%', height: 48, borderRadius: 24 }}
                                type="primary"
                                themeColor={themeObj.main.blue}
                                lIcon={searching ? undefined : "search"}
                                onPress={handleSearch}
                                disabled={searching}
                            >
                                {searching ? 'Searching...' : 'Search'}
                            </ODSButton>
                        </View>

                        {/* Suggestion link if no address found or manual option */}
                        {(noAddressFound || step === 'postcode') && (
                            <TouchableOpacity
                                onPress={handleManualEntry}
                                style={{ marginTop: 16, alignSelf: 'flex-start' }}
                            >
                                <ODSText type="sm-bold" color="#1FA6B2">
                                    {noAddressFound
                                        ? "Can't find your address? Enter address manually"
                                        : "Enter address manually"}
                                </ODSText>
                            </TouchableOpacity>
                        )}
                    </View>

                    {/* Dropdown Section (Step 2 & Step 3) */}
                    {(step === 'dropdown' || (step === 'form' && dropdownData.length > 0)) && (
                        <View style={{ marginBottom: 20 }}>
                            <ODSSelectDropdown
                                label="Select address"
                                placeholder="Select one"
                                data={dropdownData}
                                value={selectedDropdownItem}
                                error={getFieldError('selectedAddress')}
                                onSelect={(selected) => handleSelectAddress(selected)}
                            />
                        </View>
                    )}

                    {/* Form Block (Step 3 - when address is selected or entered) */}
                    {step === 'form' && (
                        <View style={{ width: '100%' }}>
                            <View style={styles(themeObj).divider} />

                            <View style={styles(themeObj).inputGroup}>
                                <ODSInput
                                    label="Address Line 1"
                                    value={addressLine1}
                                    error={!!getFieldError('addressLine1')}
                                    hints={
                                        getFieldError('addressLine1')
                                            ? [{ hint: getFieldError('addressLine1')!, state: 'error' }]
                                            : undefined
                                    }
                                    onChange={(e: any) => {
                                        setAddressLine1(e?.target?.value ?? e);
                                        if (errors.addressLine1) setErrors((prev) => ({ ...prev, addressLine1: undefined }));
                                    }}
                                    onChangeText={(t: string) => {
                                        setAddressLine1(t);
                                        if (errors.addressLine1) setErrors((prev) => ({ ...prev, addressLine1: undefined }));
                                    }}
                                />
                            </View>

                            <View style={styles(themeObj).inputGroup}>
                                <ODSInput
                                    label="Address Line 2"
                                    value={addressLine2}
                                    error={!!getFieldError('addressLine2')}
                                    hints={
                                        getFieldError('addressLine2')
                                            ? [{ hint: getFieldError('addressLine2')!, state: 'error' }]
                                            : undefined
                                    }
                                    onChange={(e: any) => {
                                        setAddressLine2(e?.target?.value ?? e);
                                        if (errors.addressLine2) setErrors((prev) => ({ ...prev, addressLine2: undefined }));
                                    }}
                                    onChangeText={(t: string) => {
                                        setAddressLine2(t);
                                        if (errors.addressLine2) setErrors((prev) => ({ ...prev, addressLine2: undefined }));
                                    }}
                                />
                            </View>

                            <View style={styles(themeObj).inputGroup}>
                                <ODSInput
                                    label="City"
                                    value={city}
                                    error={!!getFieldError('city')}
                                    hints={
                                        getFieldError('city')
                                            ? [{ hint: getFieldError('city')!, state: 'error' }]
                                            : undefined
                                    }
                                    onChange={(e: any) => {
                                        setCity(e?.target?.value ?? e);
                                        if (errors.city) setErrors((prev) => ({ ...prev, city: undefined }));
                                    }}
                                    onChangeText={(t: string) => {
                                        setCity(t);
                                        if (errors.city) setErrors((prev) => ({ ...prev, city: undefined }));
                                    }}
                                />
                            </View>

                            <View style={styles(themeObj).inputGroup}>
                                <ODSInput
                                    label="State/County"
                                    value={county}
                                    error={!!getFieldError('county')}
                                    hints={
                                        getFieldError('county')
                                            ? [{ hint: getFieldError('county')!, state: 'error' }]
                                            : undefined
                                    }
                                    onChange={(e: any) => {
                                        setCounty(e?.target?.value ?? e);
                                        if (errors.county) setErrors((prev) => ({ ...prev, county: undefined }));
                                    }}
                                    onChangeText={(t: string) => {
                                        setCounty(t);
                                        if (errors.county) setErrors((prev) => ({ ...prev, county: undefined }));
                                    }}
                                />
                            </View>

                            <View style={styles(themeObj).inputGroup}>
                                <ODSInput
                                    label="Country"
                                    value={country}
                                    error={!!getFieldError('country')}
                                    hints={
                                        getFieldError('country')
                                            ? [{ hint: getFieldError('country')!, state: 'error' }]
                                            : undefined
                                    }
                                    onChange={(e: any) => {
                                        setCountry(e?.target?.value ?? e);
                                        if (errors.country) setErrors((prev) => ({ ...prev, country: undefined }));
                                    }}
                                    onChangeText={(t: string) => {
                                        setCountry(t);
                                        if (errors.country) setErrors((prev) => ({ ...prev, country: undefined }));
                                    }}
                                />
                            </View>

                            <View style={styles(themeObj).inputGroup}>
                                <ODSInput
                                    label="Post Code"
                                    value={postCode}
                                    error={!!getFieldError('postCode')}
                                    hints={
                                        getFieldError('postCode')
                                            ? [{ hint: getFieldError('postCode')!, state: 'error' }]
                                            : undefined
                                    }
                                    onChange={(e: any) => {
                                        setPostCode(e?.target?.value ?? e);
                                        if (errors.postCode) setErrors((prev) => ({ ...prev, postCode: undefined }));
                                    }}
                                    onChangeText={(t: string) => {
                                        setPostCode(t);
                                        if (errors.postCode) setErrors((prev) => ({ ...prev, postCode: undefined }));
                                    }}
                                />
                            </View>

                            {/* Confirm Button */}
                            <View style={{ marginTop: 24, marginBottom: 10 }}>
                                <ODSButton
                                    restStyle={{ width: '100%', height: 48, borderRadius: 24 }}
                                    type="primary"
                                    themeColor={tenantConfirm ? '#28B967' : themeObj.main.blue}
                                    rIcon={tenantConfirm ? 'TB-Check' : undefined}
                                    onPress={handleConfirm}
                                    disabled={tenantConfirm || submitting}
                                >
                                    {tenantConfirm ? 'Confirmed' : (submitting ? 'Confirming...' : 'Confirm')}
                                </ODSButton>
                            </View>
                        </View>
                    )}
                </View>
            )}
        </View>
    );
};

const styles = (theme: any) =>
    StyleSheet.create({
        containerStyle: {
            width: getScreenType() == 'web' ? 680 : '100%',
            borderRadius: 12,
            shadowColor: '#101828',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.1,
            shadowRadius: 2,
            elevation: 2,
            backgroundColor: theme.neutral['white'],
            paddingVertical: 32,
            paddingHorizontal: 28,
            borderWidth: 1,
            borderColor: '#D0D6DD',
            alignSelf: 'center',
            marginBottom: 100,
        },
        readOnlyFieldGroup: {
            marginBottom: 16,
        },
        outlineButton: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: 48,
            borderRadius: 24,
            borderWidth: 1,
            borderColor: '#1FA6B2',
            marginTop: 16,
        },
        inputGroup: {
            marginBottom: 14,
        },
        divider: {
            height: 1,
            backgroundColor: '#E5E7EB',
            width: '100%',
            marginVertical: 20,
        },
    });

export default ForwardMessegeBox;